import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { ticketAlertsAPI } from '../../../services/api';

const PLATFORM_LABELS = { slack: 'Slack', gchat: 'Google Chat', teams: 'Microsoft Teams' };

/**
 * "Card open tickets that have no card."
 *
 * Two steps on purpose: count first, then post. The count is what lets
 * somebody decide whether forty cards landing in a channel is what they want.
 */
export default function TicketAlertsCatchUp({ enabled }) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(14);
  const [preview, setPreview] = useState(null);

  const previewMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.catchUpPreview(days).then((r) => r.data),
    onSuccess: (data) => setPreview(data),
  });
  const runMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.catchUp(days).then((r) => r.data),
    onSuccess: () => {
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-events'] });
    },
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Posts a card for every open ticket updated recently that has no card where its board alerts — tickets
        that came back before this was fixed, or whose card failed to post. Each card says it
        is a returning ticket. Cards are paced a couple of seconds apart per channel, so the catch-up does not trip the
        platform&rsquo;s rate limit.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Updated in the last (days)</label>
          <input
            type="number"
            min={1}
            max={90}
            className="input w-32"
            value={days}
            onChange={(e) => {
              setDays(Math.min(90, Math.max(1, parseInt(e.target.value, 10) || 14)));
              setPreview(null);
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => previewMutation.mutate()}
          disabled={previewMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
        >
          {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Count tickets without a card
        </button>
        {preview && preview.total > 0 && (
          <button
            type="button"
            onClick={() => runMutation.mutate()}
            disabled={!enabled || runMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {runMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Card {preview.total} ticket{preview.total === 1 ? '' : 's'}
          </button>
        )}
      </div>
      {!enabled && <p className="text-xs text-amber-600 dark:text-amber-400">Alerts are switched off, so a catch-up would be skipped. Turn them on and save first.</p>}

      {previewMutation.isError && (
        <p className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" /> {previewMutation.error?.response?.data?.error || 'Could not count tickets.'}
        </p>
      )}
      {runMutation.isError && (
        <p className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" /> {runMutation.error?.response?.data?.error || 'Could not queue the catch-up.'}
        </p>
      )}
      {runMutation.isSuccess && (
        <p className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          {runMutation.data.queued
            ? `Queued ${runMutation.data.queued} card${runMutation.data.queued === 1 ? '' : 's'}; they will finish posting in about ${runMutation.data.minutes} minute${runMutation.data.minutes === 1 ? '' : 's'}.`
            : 'Nothing to card — every open ticket already has one.'}
        </p>
      )}

      {preview && (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
              <tr>
                <th className="px-3 py-2">Board</th>
                <th className="px-3 py-2">Where</th>
                <th className="px-3 py-2">Open</th>
                <th className="px-3 py-2">No card</th>
                <th className="px-3 py-2">Tickets</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {preview.routes.map((r) => (
                <tr key={r.routeId} className="text-neutral-700 dark:text-neutral-300">
                  <td className="px-3 py-2">{r.boardName}</td>
                  <td className="px-3 py-2">
                    {PLATFORM_LABELS[r.platform] || r.platform} · {r.channelName}
                  </td>
                  <td className="px-3 py-2">{r.skipped ? '—' : r.open}</td>
                  <td className="px-3 py-2 font-medium">{r.skipped ? '—' : r.cardless.length}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500 dark:text-neutral-400">
                    {r.skipped
                      ? r.skipped
                      : r.cardless.slice(0, 8).map((t) => `#${t.id}`).join(', ') + (r.cardless.length > 8 ? ` and ${r.cardless.length - 8} more` : '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.total === 0 && (
            <p className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">Every open ticket updated since {preview.since} already has a card.</p>
          )}
        </div>
      )}
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Technicians can do the same from chat: <code>tickets catchup</code> counts, <code>tickets catchup go</code> posts —
        for that chat platform&rsquo;s channels only.
      </p>
    </div>
  );
}
